import React, { useEffect, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    Timestamp,
    where,
    writeBatch,
    updateDoc
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    IconButton,
    Stack,
    Pagination,
    Chip,
    InputAdornment,
    Tabs,
    Tab,
    Tooltip,
    Button,
    CircularProgress,
    Avatar,
    Grid
} from "@mui/material";

import {
    Visibility,
    Close,
    Search as SearchIcon,
    Hotel,
    EventAvailable,
    ExitToApp,
    CheckCircle,
    SwapHoriz,
    AutoFixHigh,
    PhoneIphone,
    MeetingRoomOutlined,
    AccountBalanceWalletOutlined
} from "@mui/icons-material";

// Premium Dark Theme Palette
const THEME = {
    BG_DARK: "#06362D",
    PANEL_DARK: "#06362D",
    DEEP_DARK: "#04241E",
    BORDER_DARK: "#D1A505",
    TEXT_PRIMARY: "#DEE2B1",
    TEXT_SECONDARY: "#D1A505",
    NEON_GREEN: "#FFD500", // Accent Gold / Highlight
    STRICT_DARK_BORDER: "rgba(0, 0, 0, 0.5)",
    DANGER: "#EF4444",
    DANGER_BG: "rgba(239, 68, 68, 0.15)",
    SUCCESS: "#10B981",
    SUCCESS_BG: "rgba(16, 185, 129, 0.15)"
};

function Viewreservations() {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        background: THEEP_DARK_BG(THEME.DEEP_DARK),
        color: THEME.TEXT_PRIMARY,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
    });

    function THEEP_DARK_BG(color) {
        return color;
    }

    const [bookings, setbookings] = useState([]);
    const [propertyId, setpropertyId] = useState(null);
    const [username, setusername] = useState("");
    const [userData, setUserData] = useState(null);
    const [searchText, setsearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [currentTab, setCurrentTab] = useState(0); // 0: Live, 1: Upcoming, 2: Checkout
    const [isAutoCorrecting, setIsAutoCorrecting] = useState(false);
    const [hasAttemptedAutoCorrect, setHasAttemptedAutoCorrect] = useState(false);

    // 1️⃣ Auth and fetch user credentials
    useEffect(() => {
        return auth.onAuthStateChanged(async (user) => {
            if (!user) return;
            try {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) {
                    const data = snap.data();
                    setpropertyId(data.propertyId);
                    setusername(data.username || data.name || "Staff");
                    setUserData({ uid: user.uid, ...data });
                }
            } catch (error) {
                console.error("Error fetching user role context:", error);
            }
        });
    }, []);

    // 2️⃣ Realtime Firestore listener
    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, 'reservations'),
            where('propertyId', '==', propertyId),
            where('status', 'in', ['Booked', 'Paid']),
            orderBy('checkindate', 'asc')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedBookings = querySnapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data()
            }));
            setbookings(fetchedBookings);
        }, (error) => {
            console.error("Firestore subscription error:", error);

            if (error.code === 'failed-precondition') {
                const fallbackQuery = query(
                    collection(db, 'reservations'),
                    where('propertyId', '==', propertyId),
                    where('status', 'in', ['Booked', 'Paid'])
                );

                return onSnapshot(fallbackQuery, (fallbackSnapshot) => {
                    const fetchedBookings = fallbackSnapshot.docs.map(doc => ({
                        id: doc.id,
                        data: doc.data()
                    })).sort((a, b) => new Date(a.data.checkindate) - new Date(b.data.checkindate));
                    setbookings(fetchedBookings);
                });
            }

            Toast.fire({
                icon: "error",
                title: "Failed to sync reservations data."
            });
        });

        return unsubscribe;
    }, [propertyId]);

    // 3️⃣ Overlapping room detection
    const conflictBookingIds = useMemo(() => {
        const roomDateMap = {};
        const conflicts = new Set();

        bookings.forEach(b => {
            const checkInDate = b.data?.checkindate;
            const rooms = b.data?.rooms || [];

            rooms.forEach(r => {
                const roomNo = r.roomno;
                if (!roomNo || !checkInDate) return;
                const key = `${checkInDate}_${roomNo}`;

                if (roomDateMap[key]) {
                    conflicts.add(roomDateMap[key]);
                    conflicts.add(b.id);
                } else {
                    roomDateMap[key] = b.id;
                }
            });
        });

        return conflicts;
    }, [bookings]);

    // 4️⃣ Automatic conflict detection trigger
    useEffect(() => {
        if (bookings.length > 0 && conflictBookingIds.size > 0 && !hasAttemptedAutoCorrect && userData) {
            setHasAttemptedAutoCorrect(true);
            handleAutoCorrectClashes(true);
        }
    }, [bookings, conflictBookingIds, userData]);

    // 5️⃣ Deduplication and status bucket segregation
    const categorizedData = useMemo(() => {
        const today = moment().startOf('day');

        const uniqueMap = new Map();
        bookings.forEach(item => {
            const custName = (item.data?.customers?.custname || "").trim().toLowerCase();
            const checkIn = item.data?.checkindate || "";
            const roomNo = (item.data?.rooms || []).map(r => r.roomno).sort().join(",");
            const totalAmount = item.data?.totalamount || 0;
            const bookingSource = (item.data?.bookingsource || "").trim().toLowerCase();

            const uniqueKey = `${custName}_${checkIn}_${roomNo}_${totalAmount}_${bookingSource}`;

            if (!uniqueMap.has(uniqueKey)) {
                uniqueMap.set(uniqueKey, item);
            }
        });

        const deduplicatedBookings = Array.from(uniqueMap.values());

        const searched = deduplicatedBookings.filter((item) => {
            const customerName = item.data?.customers?.custname || "";
            const mobileNo = item.data?.customers?.mobile || "";
            const bookingSource = item.data?.bookingsource || "";
            const targetRooms = (item.data?.rooms || []).map(r => r.roomno || "").join(" ");
            const combinedSearchString = `${customerName} ${mobileNo} ${bookingSource} ${targetRooms}`.toLowerCase();
            return combinedSearchString.includes(searchText.toLowerCase());
        });

        const live = [];
        const upcoming = [];
        const checkout = [];

        searched.forEach(b => {
            const checkIn = moment(b.data?.checkindate, 'YYYY-MM-DD').startOf('day');
            const checkOut = moment(b.data?.checkoutdate, 'YYYY-MM-DD').startOf('day');
            const isComing = b.data?.coming === "Yes";

            if (!isComing) {
                if (checkIn.isAfter(today)) {
                    upcoming.push(b);
                } else if (checkIn.isSameOrBefore(today) && checkOut.isSameOrBefore(today)) {
                    checkout.push(b);
                } else {
                    live.push(b);
                }
            } else {
                if (checkOut.isSameOrBefore(today)) {
                    checkout.push(b);
                } else {
                    live.push(b);
                }
            }
        });

        const sortDesc = (arr) => arr.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.data?.createdAt || 0);
            const dateB = new Date(b.createdAt || b.data?.createdAt || 0);
            return dateB - dateA;
        });

        return {
            all: deduplicatedBookings,
            live: sortDesc(live),
            upcoming: sortDesc(upcoming),
            checkout: sortDesc(checkout)
        };
    }, [bookings, searchText]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, currentTab]);

    const activeDataArray = currentTab === 0 ? categorizedData.live : currentTab === 1 ? categorizedData.upcoming : categorizedData.checkout;

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = activeDataArray.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(activeDataArray.length / rowsPerPage);

    const totalOutstanding = useMemo(() => {
        return activeDataArray.reduce((acc, curr) => acc + (Number(curr.data?.remaining) || 0), 0);
    }, [activeDataArray]);

    const handleAutoCorrectClashes = async (isBackground = false) => {
        if (conflictBookingIds.size === 0) {
            if (!isBackground) {
                Toast.fire({ icon: 'info', title: 'No room clashes found!' });
            }
            return;
        }

        if (!isBackground) {
            const result = await Swal.fire({
                title: 'Auto-Correct Room Clashes?',
                text: `Identified ${conflictBookingIds.size} conflicting room allocations. Overlaps will be assigned sequentially to the next open rooms.`,
                icon: 'warning',
                background: THEME.DEEP_DARK,
                color: THEME.TEXT_PRIMARY,
                showCancelButton: true,
                confirmButtonColor: THEME.BORDER_DARK,
                cancelButtonColor: THEME.STRICT_DARK_BORDER,
                confirmButtonText: 'Auto-Correct Now'
            });
            if (!result.isConfirmed) return;
        }

        setIsAutoCorrecting(true);

        try {
            const batch = writeBatch(db);
            const occupiedMap = {};
            bookings.forEach(b => {
                const checkInDate = b.data?.checkindate;
                (b.data?.rooms || []).forEach(r => {
                    if (r.roomno && checkInDate) {
                        if (!occupiedMap[checkInDate]) occupiedMap[checkInDate] = new Set();
                        occupiedMap[checkInDate].add(String(r.roomno));
                    }
                });
            });

            for (const bookingId of conflictBookingIds) {
                const bookingRef = doc(db, "reservations", bookingId);
                const bookingSnap = await getDoc(bookingRef);
                if (!bookingSnap.exists()) continue;

                const bData = bookingSnap.data();
                const checkInDate = bData.checkindate;
                if (!checkInDate) continue;

                if (!occupiedMap[checkInDate]) occupiedMap[checkInDate] = new Set();

                let baseRoomNum = 101;
                while (occupiedMap[checkInDate].has(String(baseRoomNum))) {
                    baseRoomNum++;
                }

                const newRoomNo = String(baseRoomNum);
                occupiedMap[checkInDate].add(newRoomNo);

                const updatedRooms = (bData.rooms || []).map((r, idx) => {
                    if (idx === 0) {
                        return { ...r, roomno: newRoomNo };
                    }
                    return r;
                });

                batch.update(bookingRef, { rooms: updatedRooms });
            }

            await batch.commit();
            Toast.fire({ icon: 'success', title: 'Room clashes resolved successfully!' });
        } catch (error) {
            console.error("Auto-correct error:", error);
            Toast.fire({ icon: 'error', title: 'Resolution failed', text: error.message });
        } finally {
            setIsAutoCorrecting(false);
        }
    };

    const handleResolveConflict = async (bookingId) => {
        const { value: newRoomNo } = await Swal.fire({
            title: 'Reassign Room',
            text: 'Enter a replacement room number for this booking:',
            input: 'text',
            inputPlaceholder: 'e.g. 204',
            background: THEME.DEEP_DARK,
            color: THEME.TEXT_PRIMARY,
            showCancelButton: true,
            confirmButtonColor: THEME.BORDER_DARK,
            cancelButtonColor: THEME.STRICT_DARK_BORDER,
            inputValidator: (value) => {
                if (!value) return 'Please specify a room number!';
            }
        });

        if (newRoomNo) {
            try {
                const bookingRef = doc(db, "reservations", bookingId);
                const bookingSnap = await getDoc(bookingRef);
                if (!bookingSnap.exists()) return;

                const bookingData = bookingSnap.data();
                const updatedRooms = (bookingData.rooms || []).map((r, idx) => {
                    if (idx === 0) {
                        return { ...r, roomno: newRoomNo };
                    }
                    return r;
                });

                await updateDoc(bookingRef, { rooms: updatedRooms });
                Toast.fire({ icon: 'success', title: 'Room reassigned successfully!' });
            } catch (error) {
                console.error("Reassign error:", error);
                Toast.fire({ icon: 'error', title: 'Failed to update assignment' });
            }
        }
    };

    const cancelBooking = async (bookingId) => {
        if (!userData) {
            Toast.fire({ icon: "error", title: "Action denied. Staff context required." });
            return;
        }

        const result = await Swal.fire({
            title: 'Cancel Reservation?',
            text: "This booking will be marked Canceled and all reserved rooms released.",
            icon: 'warning',
            background: THEME.DEEP_DARK,
            color: THEME.TEXT_PRIMARY,
            showCancelButton: true,
            confirmButtonColor: THEME.DANGER,
            cancelButtonColor: THEME.STRICT_DARK_BORDER,
            confirmButtonText: 'Yes, Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            const bookingRef = doc(db, "reservations", bookingId);
            const bookingSnap = await getDoc(bookingRef);

            if (!bookingSnap.exists()) {
                Toast.fire({ icon: "error", title: "Booking not found." });
                return;
            }

            const bookingData = bookingSnap.data();
            const checkinDetailsQuery = query(collection(db, 'checkindetails'), where('bookingid', '==', bookingId));
            const payHistoryQuery = query(collection(db, 'payhistory'), where('bookingid', '==', bookingId));

            const [checkinDetailsSnapshot, payHistorySnapshot] = await Promise.all([
                getDocs(checkinDetailsQuery),
                getDocs(payHistoryQuery)
            ]);

            const batch = writeBatch(db);
            batch.update(bookingRef, { status: "Canceled" });

            checkinDetailsSnapshot.docs.forEach(item => {
                batch.update(doc(db, "checkindetails", item.id), { status: "Canceled" });
            });
            payHistorySnapshot.docs.forEach(item => {
                batch.update(doc(db, "payhistory", item.id), { status: "Canceled" });
            });

            const logRef = doc(collection(db, 'logs'));
            batch.set(logRef, {
                bookingid: bookingId,
                customerId: bookingData.customerId || "",
                customers: bookingData.customers || {},
                rooms: bookingData.rooms || [],
                uid: userData.uid,
                username: username,
                createdAt: Timestamp.now(),
                type: 'Booking Cancellation',
            });

            await batch.commit();
            Toast.fire({ icon: "success", title: 'Booking canceled successfully' });
        } catch (error) {
            console.error("Cancellation error:", error);
            Toast.fire({ icon: "error", title: 'Failed to cancel', text: error.message });
        }
    };

    const handleCheckIn = async (bookingId) => {
        if (!userData) return;

        const result = await Swal.fire({
            title: 'Perform Check-in?',
            text: "Mark guest as arrived and shift this record into Live Occupancy.",
            icon: 'question',
            background: THEME.DEEP_DARK,
            color: THEME.TEXT_PRIMARY,
            showCancelButton: true,
            confirmButtonColor: THEME.BORDER_DARK,
            cancelButtonColor: THEME.STRICT_DARK_BORDER,
            confirmButtonText: 'Confirm Check-in'
        });

        if (result.isConfirmed) {
            try {
                const bookingRef = doc(db, "reservations", bookingId);
                await updateDoc(bookingRef, { coming: "Yes" });

                Toast.fire({ icon: "success", title: 'Guest marked as Checked-in!' });
                setCurrentTab(0);
            } catch (error) {
                console.error("Check-in error:", error);
                Toast.fire({ icon: "error", title: 'Check-in failed', text: error.message });
            }
        }
    };

    return (
        <Box sx={{ display: "flex", backgroundColor: THEME.BG_DARK, minHeight: "100vh", color: THEME.TEXT_PRIMARY }}>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, md: 3.5 },
                    position: "relative",
                    backgroundColor: THEME.BG_DARK
                }}
            >
                {/* Full-screen Auto-Correct Overlay */}
                {isAutoCorrecting && (
                    <Box
                        sx={{
                            position: "fixed",
                            inset: 0,
                            backgroundColor: "rgba(4, 36, 30, 0.85)",
                            backdropFilter: "blur(6px)",
                            zIndex: 9999,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2
                        }}
                    >
                        <CircularProgress size={52} sx={{ color: THEME.NEON_GREEN }} thickness={4} />
                        <Typography variant="h6" fontWeight={700} sx={{ color: THEME.TEXT_PRIMARY, letterSpacing: 0.5 }}>
                            Optimizing Room Allocation...
                        </Typography>
                        <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
                            Resolving double-bookings into vacant units
                        </Typography>
                    </Box>
                )}

                {/* Header Section */}
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={3}>
                    <Box>
                        <Typography variant="h4" fontWeight={800} sx={{ color: THEME.TEXT_PRIMARY, letterSpacing: "-0.02em" }}>
                            Reservations Desk
                        </Typography>
                        <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, mt: 0.5 }}>
                            Real-time view of hotel occupancy, scheduled arrivals, and departures
                        </Typography>
                    </Box>

                    {conflictBookingIds.size > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<AutoFixHigh sx={{ color: THEME.DEEP_DARK }} />}
                            onClick={() => handleAutoCorrectClashes(false)}
                            disabled={isAutoCorrecting}
                            sx={{
                                textTransform: "none",
                                fontWeight: 800,
                                px: 2.5,
                                py: 1,
                                borderRadius: "10px",
                                backgroundColor: THEME.NEON_GREEN,
                                color: THEME.DEEP_DARK,
                                border: `1px solid ${THEME.BORDER_DARK}`,
                                boxShadow: `0 4px 14px ${THEME.STRICT_DARK_BORDER}`,
                                "&:hover": {
                                    backgroundColor: THEME.BORDER_DARK,
                                    color: THEME.TEXT_PRIMARY
                                }
                            }}
                        >
                            Fix Overlaps ({conflictBookingIds.size})
                        </Button>
                    )}
                </Stack>

                {/* Summary Metric Cards */}
                <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: "14px", border: `1px solid ${THEME.BORDER_DARK}`, backgroundColor: THEME.PANEL_DARK, boxShadow: `0 4px 12px ${THEME.STRICT_DARK_BORDER}` }}>
                            <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, fontWeight: 700, textTransform: "uppercase" }}>
                                            Active In-House
                                        </Typography>
                                        <Typography variant="h5" fontWeight={800} sx={{ color: THEME.NEON_GREEN, mt: 0.5 }}>
                                            {categorizedData.live.length}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: THEME.DEEP_DARK, color: THEME.NEON_GREEN, border: `1px solid ${THEME.BORDER_DARK}`, width: 44, height: 44 }}>
                                        <Hotel fontSize="small" />
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: "14px", border: `1px solid ${THEME.BORDER_DARK}`, backgroundColor: THEME.PANEL_DARK, boxShadow: `0 4px 12px ${THEME.STRICT_DARK_BORDER}` }}>
                            <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, fontWeight: 700, textTransform: "uppercase" }}>
                                            Upcoming Arrivals
                                        </Typography>
                                        <Typography variant="h5" fontWeight={800} sx={{ color: THEME.TEXT_PRIMARY, mt: 0.5 }}>
                                            {categorizedData.upcoming.length}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: THEME.DEEP_DARK, color: THEME.TEXT_PRIMARY, border: `1px solid ${THEME.BORDER_DARK}`, width: 44, height: 44 }}>
                                        <EventAvailable fontSize="small" />
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: "14px", border: `1px solid ${THEME.BORDER_DARK}`, backgroundColor: THEME.PANEL_DARK, boxShadow: `0 4px 12px ${THEME.STRICT_DARK_BORDER}` }}>
                            <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, fontWeight: 700, textTransform: "uppercase" }}>
                                            Checkouts Due
                                        </Typography>
                                        <Typography variant="h5" fontWeight={800} sx={{ color: categorizedData.checkout.length > 0 ? THEME.DANGER : THEME.TEXT_PRIMARY, mt: 0.5 }}>
                                            {categorizedData.checkout.length}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: THEME.DEEP_DARK, color: categorizedData.checkout.length > 0 ? THEME.DANGER : THEME.TEXT_SECONDARY, border: `1px solid ${THEME.BORDER_DARK}`, width: 44, height: 44 }}>
                                        <ExitToApp fontSize="small" />
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: "14px", border: `1px solid ${THEME.BORDER_DARK}`, backgroundColor: THEME.PANEL_DARK, boxShadow: `0 4px 12px ${THEME.STRICT_DARK_BORDER}` }}>
                            <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, fontWeight: 700, textTransform: "uppercase" }}>
                                            Tab Balance Due
                                        </Typography>
                                        <Typography variant="h5" fontWeight={800} sx={{ color: THEME.NEON_GREEN, mt: 0.5 }}>
                                            ₹ {totalOutstanding.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: THEME.DEEP_DARK, color: THEME.NEON_GREEN, border: `1px solid ${THEME.BORDER_DARK}`, width: 44, height: 44 }}>
                                        <AccountBalanceWalletOutlined fontSize="small" />
                                    </Avatar>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Main Management Card */}
                <Card sx={{ borderRadius: "16px", border: `1px solid ${THEME.BORDER_DARK}`, boxShadow: `0 8px 24px ${THEME.STRICT_DARK_BORDER}`, overflow: "hidden", backgroundColor: THEME.PANEL_DARK }}>
                    {/* Header Action Bar */}
                    <Box sx={{ p: 2.5, borderBottom: `1px solid ${THEME.BORDER_DARK}`, display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, gap: 2, backgroundColor: THEME.DEEP_DARK }}>
                        {/* Custom Segmented Tabs */}
                        <Box sx={{ backgroundColor: THEME.BG_DARK, p: "4px", borderRadius: "12px", display: "inline-flex", border: `1px solid ${THEME.BORDER_DARK}` }}>
                            <Tabs
                                value={currentTab}
                                onChange={(e, val) => setCurrentTab(val)}
                                sx={{
                                    minHeight: "unset",
                                    "& .MuiTabs-indicator": { display: "none" }
                                }}
                            >
                                <Tab
                                    label={`In-House (${categorizedData.live.length})`}
                                    sx={{
                                        minHeight: 38,
                                        borderRadius: "8px",
                                        fontWeight: 700,
                                        fontSize: "0.85rem",
                                        textTransform: "none",
                                        color: THEME.TEXT_SECONDARY,
                                        "&.Mui-selected": {
                                            backgroundColor: THEME.BORDER_DARK,
                                            color: THEME.DEEP_DARK,
                                            fontWeight: 800
                                        }
                                    }}
                                />
                                <Tab
                                    label={`Upcoming (${categorizedData.upcoming.length})`}
                                    sx={{
                                        minHeight: 38,
                                        borderRadius: "8px",
                                        fontWeight: 700,
                                        fontSize: "0.85rem",
                                        textTransform: "none",
                                        color: THEME.TEXT_SECONDARY,
                                        "&.Mui-selected": {
                                            backgroundColor: THEME.BORDER_DARK,
                                            color: THEME.DEEP_DARK,
                                            fontWeight: 800
                                        }
                                    }}
                                />
                                <Tab
                                    label={`Checkouts (${categorizedData.checkout.length})`}
                                    sx={{
                                        minHeight: 38,
                                        borderRadius: "8px",
                                        fontWeight: 700,
                                        fontSize: "0.85rem",
                                        textTransform: "none",
                                        color: categorizedData.checkout.length > 0 ? THEME.DANGER : THEME.TEXT_SECONDARY,
                                        "&.Mui-selected": {
                                            backgroundColor: categorizedData.checkout.length > 0 ? THEME.DANGER : THEME.BORDER_DARK,
                                            color: "#ffffff",
                                            fontWeight: 800
                                        }
                                    }}
                                />
                            </Tabs>
                        </Box>

                        {/* Search Filter */}
                        <TextField
                            size="small"
                            placeholder="Search guest, phone, room or channel..."
                            value={searchText}
                            onChange={(e) => setsearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: THEME.TEXT_SECONDARY, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: { xs: "100%", md: 360 },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: THEME.BG_DARK,
                                    color: THEME.TEXT_PRIMARY,
                                    fontSize: "0.85rem",
                                    border: `1px solid ${THEME.BORDER_DARK}`,
                                    "& fieldset": { border: "none" }
                                }
                            }}
                        />
                    </Box>

                    {/* Table View */}
                    <TableContainer sx={{ maxHeight: "calc(100vh - 380px)", minHeight: 360, backgroundColor: THEME.PANEL_DARK }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {["#", "Actions", "Guest Details", "Channel", "Dates", "Assigned Room(s)", "Plan", "Guests", "Total Bill", "Paid", "Remaining"].map((header, idx) => (
                                        <TableCell
                                            key={header}
                                            align={idx >= 8 ? "right" : "left"}
                                            sx={{
                                                backgroundColor: THEME.DEEP_DARK,
                                                color: THEME.TEXT_SECONDARY,
                                                fontWeight: 800,
                                                fontSize: "0.75rem",
                                                borderBottom: `1px solid ${THEME.BORDER_DARK}`
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {currentRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 8, borderBottom: "none" }}>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                                <MeetingRoomOutlined sx={{ fontSize: 44, color: THEME.BORDER_DARK }} />
                                                <Typography variant="body2" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
                                                    No reservation records found for this category.
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    currentRows.map((item, index) => {
                                        const hasConflict = conflictBookingIds.has(item.id);

                                        return (
                                            <TableRow
                                                key={item.id}
                                                hover
                                                sx={{
                                                    backgroundColor: hasConflict ? "rgba(209, 165, 5, 0.12)" : THEME.PANEL_DARK,
                                                    borderLeft: hasConflict ? `4px solid ${THEME.NEON_GREEN}` : "4px solid transparent",
                                                    "&:hover": {
                                                        backgroundColor: `${THEME.DEEP_DARK} !important`
                                                    },
                                                    "& td": { borderBottom: `1px solid rgba(209, 165, 5, 0.2)` }
                                                }}
                                            >
                                                <TableCell sx={{ fontSize: "0.78rem", color: THEME.TEXT_SECONDARY, fontWeight: 600 }}>
                                                    {indexOfFirstRow + index + 1}
                                                </TableCell>

                                                <TableCell>
                                                    <Stack direction="row" spacing={0.6}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                href={`/reservation-details/${item.id}`}
                                                                sx={{
                                                                    p: 0.6,
                                                                    borderRadius: "8px",
                                                                    backgroundColor: THEME.DEEP_DARK,
                                                                    color: THEME.TEXT_PRIMARY,
                                                                    border: `1px solid ${THEME.BORDER_DARK}`,
                                                                    "&:hover": { backgroundColor: THEME.BG_DARK, color: THEME.NEON_GREEN }
                                                                }}
                                                            >
                                                                <Visibility sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {hasConflict && (
                                                            <Tooltip title="Double-booking detected! Click to reassign">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleResolveConflict(item.id)}
                                                                    sx={{
                                                                        p: 0.6,
                                                                        borderRadius: "8px",
                                                                        backgroundColor: "rgba(255, 213, 0, 0.2)",
                                                                        color: THEME.NEON_GREEN,
                                                                        border: `1px solid ${THEME.NEON_GREEN}`,
                                                                        "&:hover": { backgroundColor: THEME.NEON_GREEN, color: THEME.DEEP_DARK }
                                                                    }}
                                                                >
                                                                    <SwapHoriz sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}

                                                        {item.data?.coming !== "Yes" && (
                                                            <Tooltip title="Process Check-in">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleCheckIn(item.id)}
                                                                    sx={{
                                                                        p: 0.6,
                                                                        borderRadius: "8px",
                                                                        backgroundColor: THEME.SUCCESS_BG,
                                                                        color: THEME.SUCCESS,
                                                                        border: `1px solid ${THEME.SUCCESS}`,
                                                                        "&:hover": { backgroundColor: THEME.SUCCESS, color: THEME.DEEP_DARK }
                                                                    }}
                                                                >
                                                                    <CheckCircle sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}

                                                        <Tooltip title="Cancel Booking">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => cancelBooking(item.id)}
                                                                sx={{
                                                                    p: 0.6,
                                                                    borderRadius: "8px",
                                                                    backgroundColor: THEME.DANGER_BG,
                                                                    color: THEME.DANGER,
                                                                    border: `1px solid ${THEME.DANGER}`,
                                                                    "&:hover": { backgroundColor: THEME.DANGER, color: "#ffffff" }
                                                                }}
                                                            >
                                                                <Close sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell>
                                                    <Box>
                                                        <Typography fontWeight={700} sx={{ color: THEME.TEXT_PRIMARY, fontSize: "0.83rem" }}>
                                                            {item.data?.customers?.custname || "—"}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.72rem" }}>
                                                            <PhoneIphone sx={{ fontSize: 13 }} />
                                                            {item.data?.customers?.mobile || "No Contact"}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                <TableCell>
                                                    <Chip
                                                        label={item.data?.bookingsource || "Direct"}
                                                        size="small"
                                                        sx={{
                                                            fontSize: "0.7rem",
                                                            fontWeight: 600,
                                                            backgroundColor: THEME.DEEP_DARK,
                                                            border: `1px solid ${THEME.BORDER_DARK}`,
                                                            color: THEME.TEXT_PRIMARY
                                                        }}
                                                    />
                                                </TableCell>

                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ display: "block", color: THEME.SUCCESS, fontWeight: 700, fontSize: "0.72rem" }}>
                                                            IN: {item.data?.checkindate ? moment(item.data.checkindate).format("DD MMM, YYYY") : "—"}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: "block", color: currentTab === 2 ? THEME.DANGER : THEME.TEXT_SECONDARY, fontWeight: 700, fontSize: "0.72rem" }}>
                                                            OUT: {item.data?.checkoutdate ? moment(item.data.checkoutdate).format("DD MMM, YYYY") : "—"}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                <TableCell>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {(item.data?.rooms || []).map((r, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={`Room ${r.roomno}`}
                                                                size="small"
                                                                sx={{
                                                                    height: "22px",
                                                                    fontSize: "0.7rem",
                                                                    fontWeight: 700,
                                                                    backgroundColor: hasConflict ? "rgba(255, 213, 0, 0.25)" : THEME.DEEP_DARK,
                                                                    color: hasConflict ? THEME.NEON_GREEN : THEME.TEXT_PRIMARY,
                                                                    border: `1px solid ${hasConflict ? THEME.NEON_GREEN : THEME.BORDER_DARK}`
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>

                                                <TableCell>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {(item.data?.rooms || []).map((r, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={r.planname || "EP"}
                                                                size="small"
                                                                sx={{
                                                                    height: "22px",
                                                                    fontSize: "0.7rem",
                                                                    fontWeight: 600,
                                                                    backgroundColor: THEME.DEEP_DARK,
                                                                    color: THEME.TEXT_SECONDARY,
                                                                    border: `1px solid ${THEME.BORDER_DARK}`
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.8rem", color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
                                                    {item.data?.adults || 1}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.82rem", fontWeight: 700, color: THEME.TEXT_PRIMARY }}>
                                                    ₹ {(Number(item.data?.totalamount) || 0).toFixed(2)}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.82rem", fontWeight: 700, color: THEME.SUCCESS }}>
                                                    ₹ {(Number(item.data?.advance) || 0).toFixed(2)}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.82rem", fontWeight: 800, color: (Number(item.data?.remaining) || 0) > 0 ? THEME.DANGER : THEME.TEXT_SECONDARY }}>
                                                    ₹ {(Number(item.data?.remaining) || 0).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination Footer */}
                    {totalPages > 1 && (
                        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${THEME.BORDER_DARK}`, backgroundColor: THEME.DEEP_DARK }}>
                            <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY }}>
                                Showing {indexOfFirstRow + 1}–{Math.min(indexOfLastRow, activeDataArray.length)} of {activeDataArray.length} items
                            </Typography>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(e, v) => setCurrentPage(v)}
                                shape="rounded"
                                size="small"
                                sx={{
                                    "& .MuiPaginationItem-root": {
                                        color: THEME.TEXT_PRIMARY,
                                        borderColor: THEME.BORDER_DARK
                                    },
                                    "& .Mui-selected": {
                                        backgroundColor: `${THEME.BORDER_DARK} !important`,
                                        color: `${THEME.DEEP_DARK} !important`,
                                        fontWeight: 800
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Card>
            </Box>
        </Box>
    );
}

export default Viewreservations;